<script lang="ts" setup>
import { useDebounceFn } from '@vueuse/core'

const props = defineProps({
  attribute: {
    required: true,
    type: String as PropType<SegmentationAttribute>,
  },
  values: {
    default: () => [],
    type: Array as PropType<SegmentationValues>,
  },
})

const emit = defineEmits<{
  toggleSuggestion: [value: string]
}>()

const { t } = useI18n()
const { getIndexForAttribute } = useSegmentation()
const api = useApi()
const platformStore = usePlatformStore()

const isOpened = ref(true)
const loading = ref(false)
const nextPageReached = ref(false)
const page = ref(1)
const size = ref(100)
const suggestions = ref<Suggestion[]>([])

const form = reactive({ query: '' })

const count = computed(() => {
  switch (props.attribute) {
    case 'about.industries':
      return platformStore.industriesCount

    case 'codes.naics':
      return platformStore.industriesNaicsCount

    case 'codes.sic':
      return platformStore.industriesSicCount

    case 'locations.headquarters.city.code':
      return platformStore.citiesCount

    case 'locations.headquarters.continent.code':
      return platformStore.continentsCount

    case 'locations.headquarters.country.code':
      return platformStore.countriesCount

    case 'locations.headquarters.county.code':
      return platformStore.countiesCount

    case 'locations.headquarters.state.code':
      return platformStore.statesCount

    case 'technologies.active':
      return platformStore.technologiesCount

    default:
      return 0
  }
})

watch(
  () => form.query,
  useDebounceFn(() => {
    clearSuggestions()
    fetchSuggestions()
  }, 250),
)

onMounted(async () => {
  await fetchSuggestions()
})

function clearSuggestions() {
  suggestions.value = []
  page.value = 1
  nextPageReached.value = false
}

async function fetchSuggestions() {
  try {
    loading.value = true

    const index = getIndexForAttribute(props.attribute)

    if (index) {
      const { data: { suggestions: suggestionsFetched } } = await api.suggestions.fetch({
        index,
        page: page.value,
        search: form.query,
        size: size.value,
      })

      if (suggestionsFetched.length < size.value) {
        nextPageReached.value = true
      }

      suggestions.value.push(...suggestionsFetched)
    }
    else {
      nextPageReached.value = true
    }
  }
  catch (error) {
    console.error(error)
  }

  loading.value = false
}

async function onFetchNextPage() {
  page.value += 1

  await fetchSuggestions()
}

function onToggleSuggestion(value: string) {
  emit('toggleSuggestion', value)
}
</script>

<template>
  <UDashboardSlideover
    v-model="isOpened"
    :title="t(`title.${attribute.replaceAll('.', '-')}`, { count: numbro(count) })"
  >
    <UInput
      v-model="form.query"
      :autocomplete="false"
      class="mb-4"
      :icon="getIcon('search')"
      :loading="loading"
      :placeholder="t('globals.enterValue')"
      size="lg"
    />

    <template v-if="!loading || page > 1">
      <template v-if="suggestions.length">
        <SegmentationSuggestion
          v-for="suggestion in suggestions"
          :key="suggestion.value"
          :attribute="attribute"
          class="mb-2"
          :companies-count="suggestion.companiesCount"
          :flag="suggestion.flag"
          :icon="getIconForAttribute(attribute)"
          :label="suggestion.label"
          :value="suggestion.value"
          :values="values"
          @toggle="onToggleSuggestion"
        />

        <UButton
          v-if="!nextPageReached"
          block
          class="mt-4"
          :label="t(`show.${attribute.replaceAll('.', '-')}`)"
          :loading="loading"
          size="lg"
          :ui="{ icon: { base: 'text-green-600 dark:text-green-400' } }"
          @click="onFetchNextPage"
        />
      </template>

      <BaseMessage
        v-else
        :description="t('empty.description')"
        has-borders
        :title="t('empty.title', { search: form.query })"
      />
    </template>

    <template v-else>
      <SegmentationSuggestion
        v-for="index in 10"
        :key="index"
        class="mb-2 last:mb-0"
        loading
      />
    </template>
  </UDashboardSlideover>
</template>

<i18n lang="json">
{
  "en": {
    "empty": {
      "description": "You can contact us if you need a file with all our values.",
      "title": "No suggestion found for {search}"
    },
    "show": {
      "about-businessType": "Show the next business types",
      "about-industries": "Show the next industries",
      "about-industry": "Show the next main industries",
      "about-totalEmployees": "Show the next total employees",
      "analytics-monthlyVisitors": "Show the next monthly visitors",
      "codes-naics": "Show the next NAICS industries",
      "codes-sic": "Show the next SIC industries",
      "domain-tld": "Show the next domains",
      "finances-revenue": "Show the next revenues",
      "finances-stockExchange": "Show the next stock exchanges",
      "locations-headquarters-city-code": "Show the next cities",
      "locations-headquarters-continent-code": "Show the next continents",
      "locations-headquarters-country-code": "Show the next countries",
      "locations-headquarters-county-code": "Show the next counties",
      "locations-headquarters-state-code": "Show the next states",
      "socials": "Show the next social networks",
      "technologies-active": "Show the next technologies"
    },
    "title": {
      "about-businessType": "Our criteria for business type",
      "about-industries": "Browse {count} industries",
      "about-industry": "Browse our static industry classification",
      "about-totalEmployees": "Our criteria for total employees",
      "analytics-monthlyVisitors": "Our criteria for monthly visitors",
      "codes-naics": "Browse {count} NAICS industries",
      "codes-sic": "Browse {count} SIC industries",
      "domain-tld": "Browse {count} domains",
      "finances-revenue": "Our criteria for revenue",
      "finances-stockExchange": "Our criteria for stock exchanges",
      "locations-headquarters-city-code": "Browse {count} cities",
      "locations-headquarters-continent-code": "Browse {count} continents",
      "locations-headquarters-country-code": "Browse {count} countries",
      "locations-headquarters-county-code": "Browse {count} counties",
      "locations-headquarters-state-code": "Browse {count} states",
      "socials": "Our criteria for social networks",
      "technologies-active": "Browse {count} technologies"
    }
  },
  "es": {
    "empty": {
      "description": "Puede contactarnos si necesita un archivo con todos nuestros valores.",
      "title": "No se encontraron sugerencias para {search}"
    },
    "show": {
      "about-businessType": "Mostrar los siguientes tipos de negocio",
      "about-industries": "Mostrar las siguientes industrias",
      "about-industry": "Mostrar las siguientes industrias principales",
      "about-totalEmployees": "Mostrar los siguientes totales de empleados",
      "analytics-monthlyVisitors": "Mostrar los siguientes visitantes mensuales",
      "codes-naics": "Mostrar las siguientes industrias NAICS",
      "codes-sic": "Mostrar las siguientes industrias SIC",
      "domain-tld": "Mostrar los siguientes dominios",
      "finances-revenue": "Mostrar los siguientes ingresos",
      "finances-stockExchange": "Mostrar las siguientes bolsas de valores",
      "locations-headquarters-city-code": "Mostrar las siguientes ciudades",
      "locations-headquarters-continent-code": "Mostrar los siguientes continentes",
      "locations-headquarters-country-code": "Mostrar los siguientes países",
      "locations-headquarters-county-code": "Mostrar los siguientes condados",
      "locations-headquarters-state-code": "Mostrar los siguientes estados",
      "socials": "Mostrar las siguientes redes sociales",
      "technologies-active": "Mostrar las siguientes tecnologías"
    },
    "title": {
      "about-businessType": "Nuestros criterios para el tipo de negocio",
      "about-industries": "Explorar {count} industrias",
      "about-industry": "Explorar nuestra clasificación de industrias estáticas",
      "about-totalEmployees": "Nuestros criterios para el total de empleados",
      "analytics-monthlyVisitors": "Nuestros criterios para visitantes mensuales",
      "codes-naics": "Explorar {count} industrias NAICS",
      "codes-sic": "Explorar {count} industrias SIC",
      "domain-tld": "Explorar {count} dominios",
      "finances-revenue": "Nuestros criterios para ingresos",
      "finances-stockExchange": "Nuestros criterios para bolsas de valores",
      "locations-headquarters-city-code": "Explorar {count} ciudades",
      "locations-headquarters-continent-code": "Explorar {count} continentes",
      "locations-headquarters-country-code": "Explorar {count} países",
      "locations-headquarters-county-code": "Explorar {count} condados",
      "locations-headquarters-state-code": "Explorar {count} estados",
      "socials": "Nuestros criterios para redes sociales",
      "technologies-active": "Explorar {count} tecnologías"
    }
  },
  "fr": {
    "empty": {
      "description": "Vous pouvez nous contacter si vous avez besoin d'un fichier avec toutes nos valeurs.",
      "title": "Aucune suggestion trouvée pour {search}"
    },
    "show": {
      "about-businessType": "Afficher les prochaines types d'entreprise",
      "about-industries": "Afficher les prochaines industries",
      "about-industry": "Afficher les prochaines industries principales",
      "about-totalEmployees": "Afficher les prochaines total employees",
      "analytics-monthlyVisitors": "Afficher les prochaines visiteurs mensuels",
      "codes-naics": "Afficher les prochaines industries NAICS",
      "codes-sic": "Afficher les prochaines industries SIC",
      "domain-tld": "Afficher les prochains domaines",
      "finances-revenue": "Afficher les prochains chiffres d'affaires",
      "finances-stockExchange": "Afficher les prochains bourses",
      "locations-headquarters-city-code": "Afficher les prochaines villes",
      "locations-headquarters-continent-code": "Afficher les prochains continents",
      "locations-headquarters-country-code": "Afficher les prochains pays",
      "locations-headquarters-county-code": "Afficher les prochains départements",
      "locations-headquarters-state-code": "Afficher les prochains régions",
      "socials": "Afficher les prochains réseaux sociaux",
      "technologies-active": "Afficher les prochaines technologies"
    },
    "title": {
      "about-businessType": "Nos critères pour le type d'entreprise",
      "about-industries": "Parcourir {count} industries",
      "about-industry": "Parcourir notre classification d'industries statiques",
      "about-totalEmployees": "Nos critères pour le nombre d'employés",
      "analytics-monthlyVisitors": "Nos critères pour les visiteurs mensuels",
      "codes-naics": "Parcourir {count} industries NAICS",
      "codes-sic": "Parcourir {count} industries SIC",
      "domain-tld": "Parcourir {count} domaines",
      "finances-revenue": "Nos critères pour le chiffre d'affaires",
      "finances-stockExchange": "Nos critères pour les bourses",
      "locations-headquarters-city-code": "Parcourir {count} villes",
      "locations-headquarters-continent-code": "Parcourir {count} continents",
      "locations-headquarters-country-code": "Parcourir {count} pays",
      "locations-headquarters-county-code": "Parcourir {count} départements",
      "locations-headquarters-state-code": "Parcourir {count} régions",
      "socials": "Nos critères pour les réseaux sociaux",
      "technologies-active": "Parcourir {count} technologies"
    }
  }
}
</i18n>
